import { InjectionToken } from '@angular/core';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** Structured detail. Keep it small — one line per entry stays readable, ten do not. */
export type LogFields = Record<string, unknown>;

/**
 * Never pass token values, the authorization code, the PKCE verifier, the nonce or
 * anything out of `personalContactDetails` in here. Log facts *about* them instead:
 * seconds until expiry, whether a refresh token exists, a key id, an error code.
 *
 * Right now this only reaches the console, but the rule is what makes it safe to add a
 * buffer that a user can attach to a support request later.
 */
export type LogFn = (message: string, fields?: LogFields) => void;

export interface Logger {
  debug: LogFn;
  info: LogFn;
  warn: LogFn;
  error: LogFn;
}

export interface LoggerFactory {
  /** `name` is printed as the tag and is what you filter the console by. */
  scope(name: string): Logger;
}

export const LOGGER = new InjectionToken<LoggerFactory>('LOGGER');

const ORDER: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

/**
 * Anything that is not one of the four levels becomes `info`.
 *
 * The type only holds where callers are typed. A value that slips through from a config
 * string would otherwise take `console[level]` to `undefined` and throw on `.bind`, and a
 * threshold that is not in `ORDER` compares as `undefined` and switches filtering off
 * entirely. Falling back keeps the message visible instead of losing it either way.
 */
const knownLevel = (level: LogLevel): LogLevel =>
  Object.prototype.hasOwnProperty.call(ORDER, level) ? level : 'info';

/**
 * Colours are derived from the scope name so each subsystem keeps the same tint across a
 * session without anyone maintaining a table.
 */
const hueFor = (scope: string): number => {
  let hash = 0;
  for (const character of scope) hash = (hash * 31 + character.charCodeAt(0)) % 360;
  return hash;
};

const timestamp = (): string => new Date().toISOString().slice(11, 23);

const noop = (): void => undefined;

/**
 * The four levels are getters, not methods, and each hands back a *bound* console function
 * instead of calling one.
 *
 * That is what keeps the source link in DevTools useful. The console attributes an entry to
 * the JavaScript frame that invoked it — with a wrapper method that frame is always inside
 * this file, and every line points at the logger instead of at the code that logged. A
 * bound function is native, so the nearest frame is the caller and the link goes where you
 * want it.
 *
 * The price: everything the entry needs must be decided before the caller's arguments. The
 * scope tag and the timestamp are baked in here, the message and fields are appended by
 * the console as further arguments.
 */
class ConsoleLogger implements Logger {
  private readonly style = `color:hsl(${hueFor(this.scope)},70%,45%);font-weight:600`;
  private readonly threshold = ORDER[knownLevel(this.minLevel)];

  constructor(
    private readonly scope: string,
    private readonly minLevel: LogLevel,
  ) {}

  get debug(): LogFn { return this.at('debug'); }
  get info(): LogFn { return this.at('info'); }
  get warn(): LogFn { return this.at('warn'); }
  get error(): LogFn { return this.at('error'); }

  private at(level: LogLevel): LogFn {
    const effective = knownLevel(level);
    if (ORDER[effective] < this.threshold) return noop;
    // One console method per level, so the browser's own level filter keeps working.
    return console[effective].bind(
      console,
      `%c[${this.scope}]%c ${timestamp()}`,
      this.style,
      'color:inherit',
    ) as LogFn;
  }
}

export class ConsoleLoggerFactory implements LoggerFactory {
  private readonly loggers = new Map<string, Logger>();

  constructor(private readonly minLevel: LogLevel) {}

  scope(name: string): Logger {
    let logger = this.loggers.get(name);
    if (!logger) {
      logger = new ConsoleLogger(name, this.minLevel);
      this.loggers.set(name, logger);
    }
    return logger;
  }
}
